import AudioEngine from '@/webgl/audio/AudioEngine'
import Sound from '@/webgl/audio/Sound'
import Signal from '@/webgl/core/Signal'
import { TimeEntity } from '@/webgl/runner/entities/Time'
import GameWorld from '@/webgl/runner/GameWorld'
import ICollectibleTrack from '@/webgl/runner/interfaces/ICollectibleTrack'
import { IRunnerRenderer } from '@/webgl/runner/interfaces/IRunnerRenderer'
import { getAudioSegement as getAudioSegment } from '@/webgl/runner/scenarios'
import { AudioSegmentSetup, ScenarioSetup } from '@/webgl/runner/scenarios/ScenarioSetup.types'
import GateTrack from '@/webgl/runner/scenarios/tracks/GateTrack'
import ProceduralTrack from '@/webgl/runner/scenarios/tracks/ProceduralTrack'
import { IGameSegment } from '@/webgl/runner/systems/SequencingSystem'

export class AudioSegment implements IGameSegment {
  onComplete: Signal<void> = new Signal<void>()
  queue: number = -1
  activeSound: Sound | null
  world: GameWorld

  config: AudioSegmentSetup
  track: ICollectibleTrack

  constructor(
    readonly id: string,
    readonly scrollingItems: string[] = []
  ) {}

  init(world: GameWorld, renderer: IRunnerRenderer, setup: ScenarioSetup) {
    this.world = world
    this.config = getAudioSegment(setup, this.id)

    const trackCfg = this.config.track
    if (trackCfg) {
      switch (trackCfg.type) {
        case 'procedural':
          this.track = new ProceduralTrack(this.world, trackCfg)
          break
        case 'gate-track':
          this.track = new GateTrack(this.world, trackCfg)
          break
      }
    }
  }

  start(): void {
    // AudioEngine.instance.music.setVolume(0.25, 0.15);
    if (this.scrollingItems.length > 0) {
      this.world.background.spawnableItems = this.scrollingItems
    }

    if (this.config.scrolling) {
      this.world.background.spawnableItems = this.config.scrolling
    }

    this.queue = -1
    this.playNextAudio()
  }

  playNextAudio = () => {
    this.queue++
    if (this.queue == this.config.audios.length) {
      this.activeSound = null
      this.onComplete.emit()
      return
    }

    this.activeSound = AudioEngine.play(this.config.audios[this.queue], { delay: this.queue == 0 ? 0 : 0.5 })

    // AudioEngine.play returns null when the sound is unavailable (e.g. the
    // audio library was torn down while navigating away). Bail out gracefully
    // instead of dereferencing a null sound.
    if (!this.activeSound) {
      this.onComplete.emit()
      return
    }

    this.activeSound.onEnded.on(this.playNextAudio)
  }

  update(time: TimeEntity) {
    if (!this.track) return

    const { settings } = this.world

    const spawnQueue = this.track.evaluate(time.unscaledDeltaTime * settings.scrollSpeed)
    for (const trackItem of spawnQueue) {
      this.world.events.SpawnCollectible.emit({
        id: trackItem.data.id,
        lane: trackItem.lane,
        entity: trackItem.entity,
      })
    }
  }

  stop(): void {
    // AudioEngine.instance.music.setVolume(1.0, 0.15);
    this.track?.clear()

    if (this.activeSound) {
      this.activeSound.onEnded.off(this.playNextAudio)
      this.activeSound.stop()
      this.activeSound = null
    }
    this.queue = -1
  }
}
